/**
 * Shared entity detail tabs component.
 * Renders Description, Memory, and dynamic linked-entity tabs.
 *
 * Used by contacts, companies, and projects detail views.
 * Each module can wrap this with module-specific header content.
 */

import { useState, useCallback, useRef, useMemo } from "react";
import type { JSX } from "react";
import {
  SearchableTabs,
  EntitySection,
  Icon,
  IconButton,
  Stack,
  Text,
} from "../../components/ui";
import { Scrollable } from "../../components/ui";
import { MarkdownEditor } from "../../components/shared/MarkdownEditor";
import { useEditorMentionSuggestion } from "../../components/shared/markdown/useEditorMentionSuggestion";
import { ExpandableEntityCard } from "../../components/agent/ExpandableEntityCard";
import type { LinkedEntitySummary, FacetSummary } from "./sharedTypes";
import { computeDynamicTabs, buildAllTabs } from "./entityTabUtils";
import { useEntityFacet } from "./useEntityFacet";
import { useAppRuntime } from "../../providers/AppRuntimeProvider";
import { useTriggersForEntities } from "../../agent/triggers/useTriggers";
import { TriggersSection } from "../../agent/triggers/TriggersSection";
import { FilesTab } from "./FilesTab";

export interface EntityDetailTabsProps {
  readonly entityId: string;
  readonly linkedEntities: readonly LinkedEntitySummary[];
  readonly facets: readonly FacetSummary[];
  /** Facet schema ID for description (e.g. "contacts.person.description") */
  readonly descriptionSchemaId: string;
  /** Facet schema ID for memory (e.g. "contacts.person.memory") */
  readonly memorySchemaId: string;
  /** Max visible tabs before overflow "..." (default 7) */
  readonly maxVisibleTabs?: number;
  /** Enable search mode in tabs */
  readonly searchable?: boolean;
  /** Content to render above the tabs (e.g., InfoCard for companies) */
  readonly headerContent?: React.ReactNode;
  /** When provided, a "Details" tab is prepended (before Description /
   *  Memory / dynamic) and becomes the default active tab. Contacts
   *  use this for the Google-Contacts-style contact-info column. */
  readonly detailsContent?: React.ReactNode;
}

export function EntityDetailTabs({
  entityId,
  linkedEntities,
  facets: _facets,
  descriptionSchemaId,
  memorySchemaId,
  maxVisibleTabs = 7,
  searchable = false,
  headerContent,
  detailsContent,
}: EntityDetailTabsProps): JSX.Element {
  const runtime = useAppRuntime();
  const hasDetailsTab = detailsContent !== undefined && detailsContent !== null;
  const [activeTab, setActiveTab] = useState(hasDetailsTab ? "details" : "description");

  // Build module title map from runtime registry
  const moduleTitles = useMemo(() => {
    const map: Record<string, string> = {};
    for (const mod of runtime.modules.list()) {
      map[mod.id] = mod.title;
    }
    return map;
  }, [runtime.modules]);

  const dynamicTabs = computeDynamicTabs(linkedEntities, moduleTitles);
  const baseTabs = buildAllTabs(dynamicTabs);
  // When the consumer provides Overview content, the Description
  // facet is rendered inside Overview's right column — drop the
  // standalone Description tab to avoid duplication.
  const fixedFiltered = hasDetailsTab
    ? baseTabs.filter((t) => t.id !== "description")
    : baseTabs;

  // Triggers tab — surface any triggers attached to this entity OR to
  // a 1-hop watchable neighbor (e.g. the contact's email.address).
  // Backend `triggers.list_for_entity` already handles the traversal,
  // so we just pass the entity id and let the count decide visibility.
  const triggerEntityIds = useMemo(() => [entityId], [entityId]);
  const { triggers } = useTriggersForEntities(triggerEntityIds);
  const triggersTab = triggers.length > 0
    ? [{ id: "triggers", label: "Triggers" }]
    : [];

  // Files tab is always present so the operator can drop a file even
  // when nothing's attached yet. Inside, the dropzone shows above the
  // (possibly empty) list of currently-linked `file.object` entities.
  const filesTab = [{ id: "files", label: "Files" }];

  const allTabs = hasDetailsTab
    ? [{ id: "details", label: "Overview" }, ...fixedFiltered, ...triggersTab, ...filesTab]
    : [...fixedFiltered, ...triggersTab, ...filesTab];

  const attachedFiles = useMemo(
    () => linkedEntities.filter((e) => e.schema_id === "file.object"),
    [linkedEntities],
  );

  // Description facet
  const description = useEntityFacet(entityId, descriptionSchemaId);

  // Memory facet (single document, like description)
  const memory = useEntityFacet(entityId, memorySchemaId);

  const handleDescriptionChange = useCallback(
    (markdown: string) => {
      description.save({ body: markdown });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [description.save],
  );

  const handleMemoryChange = useCallback(
    (markdown: string) => {
      memory.save({ body: markdown });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [memory.save],
  );

  return (
    <Scrollable>
      {headerContent ?? null}

      <SearchableTabs
        tabs={allTabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        searchable={searchable}
        maxVisibleTabs={maxVisibleTabs}
      />

      {/* ── Tab Content ─────────────────────────────────────── */}
      <Stack gap={5}>
        {activeTab === "details" && hasDetailsTab ? (
          <div className="px-5 py-4">{detailsContent}</div>
        ) : null}

        {activeTab === "description" ? (
          <DescriptionTab
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            body={(description.data?.body as string) ?? ""}
            isLoading={description.isLoading}
            onChange={handleDescriptionChange}
          />
        ) : null}

        {activeTab === "memory" ? (
          <DescriptionTab
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            body={(memory.data?.body as string) ?? ""}
            isLoading={memory.isLoading}
            onChange={handleMemoryChange}
            placeholder="Agent memory..."
          />
        ) : null}

        {/* Dynamic tabs — linked entity lists */}
        {dynamicTabs.map((tab) =>
          activeTab === tab.id ? (
            <DynamicTabContent
              key={tab.id}
              tab={tab}
              linkedEntities={linkedEntities}
            />
          ) : null,
        )}

        {activeTab === "triggers" ? (
          <div className="px-5 py-4">
            <TriggersSection entityIds={triggerEntityIds} />
          </div>
        ) : null}

        {activeTab === "files" ? (
          <FilesTab entityId={entityId} attachedFiles={attachedFiles} />
        ) : null}
      </Stack>
    </Scrollable>
  );
}

/* ── Description Tab ──────────────────────────────────────── */

function DescriptionTab({
  body,
  isLoading,
  onChange,
  placeholder = "Add a description...",
}: {
  readonly body: string;
  readonly isLoading: boolean;
  readonly onChange: (markdown: string) => void;
  readonly placeholder?: string;
}): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [editorKey, setEditorKey] = useState(0);
  const localRef = useRef(body);
  localRef.current = body;
  const mentionSuggestion = useEditorMentionSuggestion();

  const handleToggleEdit = useCallback(() => {
    setEditing((m) => {
      // Remount the editor when we toggle so the fresh body becomes
      // the next initialValue (no stale content after save).
      setEditorKey((k) => k + 1);
      return !m;
    });
  }, []);

  if (isLoading) {
    return (
      <Stack gap={3} align="center" className="py-12">
        <Text variant="body" color="tertiary">Loading...</Text>
      </Stack>
    );
  }

  const isEmpty = !body.trim();
  // Same DOM layout in view and edit modes — only `readOnly` flips
  // and the toggle icon swaps pencil ↔ check. No toolbar bar above,
  // no padding jump. Click pencil → cursor activates in the same
  // spot the read-only text was rendered.
  const editorClass = "[&_.ProseMirror]:!p-0 [&_.milkdown-editor-wrapper]:!p-0";

  return (
    <div className="px-5 py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          {isEmpty && !editing ? (
            <div
              className="text-content-tertiary text-sm cursor-pointer hover:text-content-secondary transition-colors"
              onClick={handleToggleEdit}
            >
              {placeholder}
            </div>
          ) : (
            <MarkdownEditor
              // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
              key={`${editing ? "edit" : "view"}-${editorKey}`}
              initialValue={body}
              onChange={editing ? onChange : (): void => { /* read-only */ }}
              placeholder={placeholder}
              readOnly={!editing}
              autoFocus={editing}
              mentionSuggestion={editing ? mentionSuggestion : undefined}
              className={editorClass}
            />
          )}
        </div>
        <IconButton variant="ghost" onClick={handleToggleEdit} label={editing ? "Done" : "Edit"}>
          <Icon name={editing ? "check" : "pencil"} size={14} />
        </IconButton>
      </div>
    </div>
  );
}

/* ── Dynamic Tab Content ──────────────────────────────────── */

function DynamicTabContent({
  tab,
  linkedEntities,
}: {
  readonly tab: { readonly id: string; readonly label: string; readonly icon: string; readonly entityIds: readonly string[]; readonly groups?: readonly { readonly schemaId: string; readonly label: string; readonly icon: string; readonly entityIds: readonly string[] }[] };
  readonly linkedEntities: readonly LinkedEntitySummary[];
}): JSX.Element {
  const runtime = useAppRuntime();

  // If tab has entity type groups (subheadings), render them; otherwise flat list
  const groups = tab.groups && tab.groups.length > 0
    ? tab.groups
    : [{ schemaId: "", label: tab.label, icon: tab.icon, entityIds: tab.entityIds }];

  return (
    <Stack gap={4} className="px-5 py-4">
      {groups.map((group) => {
        const groupEntities = linkedEntities.filter((e) => group.entityIds.includes(e.id));

        // Deduplicate
        const seenNames = new Set<string>();
        const seenIds = new Set<string>();
        const deduped = groupEntities.filter((e) => {
          if (seenIds.has(e.id)) return false;
          seenIds.add(e.id);
          if (!e.name) return true;
          if (seenNames.has(e.name)) return false;
          seenNames.add(e.name);
          return true;
        });

        if (deduped.length === 0) return null;

        return (
          <EntitySection
            key={group.schemaId || group.label}
            icon={<Icon name={group.icon as Parameters<typeof Icon>[0]["name"]} size={18} className="text-content-secondary" />}
            title={group.label}
            count={groupEntities.length}
            countColor="blue"
          >
            <Stack gap={2}>
              {deduped.map((entity) => (
                <ExpandableEntityCard
                  key={entity.id}
                  schemaId={entity.schema_id}
                  data={{ id: entity.id, name: entity.name, ...entity.data }}
                  runtime={runtime}
                />
              ))}
            </Stack>
          </EntitySection>
        );
      })}
    </Stack>
  );
}
